"use client";

import { useState, useEffect, useRef } from "react";

const allBatches = [
  { id: "KR-2025-001", line: "Red Line", date: "Nov 2025", alkaloids: "1.42%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
  { id: "KR-2025-002", line: "White Line", date: "Nov 2025", alkaloids: "1.38%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
  { id: "KR-2025-003", line: "Green Line", date: "Dec 2025", alkaloids: "1.51%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
  { id: "KR-2026-001", line: "Green Line", date: "Jan 2026", alkaloids: "1.49%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
  { id: "KR-2026-002", line: "Red Line", date: "Feb 2026", alkaloids: "1.44%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
  { id: "KR-2026-003", line: "White Line", date: "Feb 2026", alkaloids: "1.40%", contaminants: "ND", heavy_metals: "Pass", mold: "ND", pesticides: "Pass", lab: "Cascadia Labs" },
];

const lineFilters = ["All", "Red Line", "White Line", "Green Line"];

export default function LabTestingInteractive() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedBatch, setSelectedBatch] = useState(allBatches[0]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const filtered = activeFilter === "All" ? allBatches : allBatches.filter((b) => b.line === activeFilter);

  return (
    <div className="py-16" style={{ backgroundColor: "#F6F5F2" }}>
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Intro */}
        <div
          ref={(el) => { refs.current[0] = el; }}
          className="reveal grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20 items-center"
        >
          <div>
            <span className="section-label mb-4 block">Our Commitment</span>
            <h2
              className="font-serif font-bold mb-4"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
                letterSpacing: "-0.025em",
                lineHeight: "1.1",
                color: "#1F1F1F",
              }}
            >
              Third-Party Testing.
              <br />
              <em style={{ color: "#3F5A45" }}>Every Batch. Public Results.</em>
            </h2>
            <p
              className="text-base leading-relaxed mb-6"
              style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              We partner with ISO/IEC 17025:2017 accredited independent laboratories to test every single batch before it ships. Results are published here — no exceptions, no cherry-picking.
            </p>
            <p
              className="text-base leading-relaxed"
              style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              Each COA covers alkaloid content, microbial contaminants, heavy metals, mold, yeast, and pesticide residues. You deserve to know exactly what you're getting.
            </p>
          </div>

          {/* What we test */}
          <div className="space-y-3">
            {[
              { label: "Alkaloid Content", desc: "Mitragynine quantification" },
              { label: "Microbial Contaminants", desc: "Salmonella, E. coli, Total Plate Count" },
              { label: "Heavy Metals", desc: "Arsenic, Cadmium, Lead, Mercury" },
              { label: "Mold & Yeast", desc: "Total yeast and mold count" },
              { label: "Pesticide Residues", desc: "Multi-residue pesticide screening" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-4 p-4"
                style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: "rgba(63,90,69,0.1)" }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="#3F5A45" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <div
                    className="text-sm font-semibold mb-0.5"
                    style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* COA Viewer */}
        <div
          ref={(el) => { refs.current[1] = el; }}
          className="reveal mb-8"
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h3
              className="font-serif font-bold"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "1.5rem",
                letterSpacing: "-0.02em",
                color: "#1F1F1F",
              }}
            >
              Batch COA Viewer
            </h3>
            {/* Filter */}
            <div className="flex gap-2">
              {lineFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide transition-all duration-200"
                  style={{
                    backgroundColor: activeFilter === f ? "#3F5A45" : "white",
                    color: activeFilter === f ? "white" : "#6B6B6B",
                    border: `1px solid ${activeFilter === f ? "#3F5A45" : "#E0DFDB"}`,
                    fontFamily: "Inter, system-ui, sans-serif",
                    borderRadius: "2px",
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-0"
            style={{ border: "1px solid #E0DFDB", backgroundColor: "white" }}
          >
            {/* Batch list */}
            <div
              className="border-b lg:border-b-0 lg:border-r p-6"
              style={{ borderColor: "#E0DFDB", backgroundColor: "#F6F5F2" }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-widest mb-4"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                {filtered.length} Batch{filtered.length !== 1 ? "es" : ""}
              </p>
              <div className="space-y-2">
                {filtered.map((batch) => (
                  <button
                    key={batch.id}
                    onClick={() => setSelectedBatch(batch)}
                    className="w-full text-left p-3 transition-all duration-200"
                    style={{
                      backgroundColor: selectedBatch.id === batch.id ? "white" : "transparent",
                      border: `1px solid ${selectedBatch.id === batch.id ? "#3F5A45" : "transparent"}`,
                      borderRadius: "2px",
                    }}
                  >
                    <div
                      className="text-xs font-semibold mb-0.5"
                      style={{
                        color: selectedBatch.id === batch.id ? "#3F5A45" : "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      {batch.id}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {batch.line} · {batch.date}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* COA Detail */}
            <div className="lg:col-span-2 p-8">
              <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                <div>
                  <div
                    className="text-xs font-semibold uppercase tracking-widest mb-1"
                    style={{ color: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    Certificate of Analysis
                  </div>
                  <h3
                    className="font-serif font-bold"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.5rem",
                      letterSpacing: "-0.02em",
                      color: "#1F1F1F",
                    }}
                  >
                    Batch {selectedBatch.id}
                  </h3>
                  <p
                    className="text-sm mt-1"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {selectedBatch.line} · Tested {selectedBatch.date} · {selectedBatch.lab}
                  </p>
                </div>
                <div
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide flex items-center gap-2"
                  style={{
                    backgroundColor: "rgba(63,90,69,0.08)",
                    color: "#3F5A45",
                    border: "1px solid rgba(63,90,69,0.2)",
                    fontFamily: "Inter, system-ui, sans-serif",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="#3F5A45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  All Tests Passed
                </div>
              </div>

              {/* Results */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {[
                  { label: "Total Alkaloids", value: selectedBatch.alkaloids, sub: "Mitragynine + 7-OH" },
                  { label: "Microbial", value: selectedBatch.contaminants, sub: "Salmonella / E. coli" },
                  { label: "Heavy Metals", value: selectedBatch.heavy_metals, sub: "As, Cd, Pb, Hg" },
                  { label: "Mold & Yeast", value: selectedBatch.mold, sub: "Total count" },
                  { label: "Pesticides", value: selectedBatch.pesticides, sub: "Multi-residue screen" },
                  { label: "Lab Accreditation", value: "ISO", sub: "17025:2017 Certified" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="p-4"
                    style={{ backgroundColor: "#F6F5F2", border: "1px solid #E0DFDB" }}
                  >
                    <div
                      className="text-xs font-medium mb-2"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.label}
                    </div>
                    <div
                      className="font-serif font-bold text-xl mb-1"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        color: item.value === "Pass" || item.value === "ND" || item.value === "ISO" ? "#3F5A45" : "#1F1F1F",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.sub}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-between p-4 flex-wrap gap-4"
                style={{ backgroundColor: "#F6F5F2", border: "1px solid #E0DFDB" }}
              >
                <div>
                  <p
                    className="text-xs font-semibold mb-0.5"
                    style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    Tested by: {selectedBatch.lab} — Portland, OR
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    ISO/IEC 17025:2017 Accredited · Fully Independent
                  </p>
                </div>
                <button
                  className="btn-ghost text-xs"
                  style={{ color: "#3F5A45", borderBottomColor: "#3F5A45" }}
                >
                  Download PDF COA
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16L7 11M12 16L17 11M12 16V4M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Process explanation */}
        <div
          ref={(el) => { refs.current[2] = el; }}
          className="reveal mt-20 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            {
              step: "01",
              title: "Batch Received",
              desc: "When a new batch arrives at our facility, we immediately pull representative samples before any product is packaged or shipped.",
            },
            {
              step: "02",
              title: "Lab Submission",
              desc: "Samples are sent to Cascadia Labs — an ISO/IEC 17025:2017 accredited independent laboratory with no affiliation to Kratapillar.",
            },
            {
              step: "03",
              title: "Results Published",
              desc: "Once results are received and reviewed, the full COA is published here. Batches that don't meet our standards are rejected.",
            },
          ].map((item) => (
            <div
              key={item.step}
              className="p-8"
              style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
            >
              <div
                className="text-xs font-semibold mb-6"
                style={{ color: "rgba(63,90,69,0.5)", fontFamily: "Inter, system-ui, sans-serif", letterSpacing: "0.1em" }}
              >
                {item.step}
              </div>
              <h3
                className="font-serif font-semibold mb-3"
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: "1.25rem",
                  letterSpacing: "-0.015em",
                  color: "#1F1F1F",
                }}
              >
                {item.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}